import type {
  Project,
  Chapter,
  BookMetadata,
  WritingGoal,
  SectionType,
  DailyStats,
} from '../types';
import type {
  Project as ApiProject,
  Chapter as ApiChapter,
  WritingGoal as ApiWritingGoal,
  DailyStats as ApiDailyStats,
} from './api';

// API response types (extended from api.ts)
export interface ApiProjectWithChapters extends ApiProject {
  chapters?: ApiChapter[];
  writingGoal?: ApiWritingGoal | null;
}

// Convert API Project to Frontend Project
export function mapProjectFromApi(apiProject: ApiProjectWithChapters): Project {
  const metadata: BookMetadata = {
    title: apiProject.title,
    subtitle: undefined,
    author: '',
    description: apiProject.description || '',
    genre: apiProject.genre || '',
    language: apiProject.language || 'en',
    coverImage: apiProject.coverImage,
  };

  const chapters: Chapter[] = (apiProject.chapters || []).map(mapChapterFromApi);

  const writingGoal: WritingGoal | undefined = apiProject.writingGoal
    ? {
        type: apiProject.writingGoal.type,
        targetWords: apiProject.writingGoal.targetWords,
        currentWords: apiProject.writingGoal.currentWords,
        deadline: apiProject.writingGoal.deadline
          ? new Date(apiProject.writingGoal.deadline)
          : undefined,
      }
    : undefined;

  return {
    id: apiProject.id,
    kind: (apiProject as { kind?: 'book' | 'manga' }).kind ?? 'book',
    metadata,
    chapters,
    writingGoal,
    sessions: [],
    createdAt: new Date(apiProject.createdAt),
    updatedAt: new Date(apiProject.updatedAt),
  };
}

// Convert Frontend Project to API format for updates
export function mapProjectToApi(project: Project): Partial<ApiProject> {
  return {
    title: project.metadata.title,
    description: project.metadata.description,
    genre: project.metadata.genre,
    language: project.metadata.language,
    coverImage: project.metadata.coverImage,
  };
}

// Convert API Chapter to Frontend Chapter
export function mapChapterFromApi(apiChapter: ApiChapter): Chapter {
  return {
    id: apiChapter.id,
    title: apiChapter.title,
    content: apiChapter.content,
    order: apiChapter.order,
    wordCount: apiChapter.wordCount,
    sectionType: apiChapter.sectionType as SectionType,
    notes: apiChapter.notes,
    image: (apiChapter as any).image, // Author photo for special pages
    createdAt: new Date(apiChapter.createdAt),
    updatedAt: new Date(apiChapter.updatedAt),
  };
}

// Convert Frontend Chapter to API format for updates
export function mapChapterToApi(
  chapter: Partial<Chapter>
): Partial<ApiChapter> {
  const result: Partial<ApiChapter> & { image?: string } = {};

  if (chapter.title !== undefined) result.title = chapter.title;
  if (chapter.content !== undefined) result.content = chapter.content;
  if (chapter.sectionType !== undefined) result.sectionType = chapter.sectionType;
  if (chapter.notes !== undefined) result.notes = chapter.notes;
  if (chapter.image !== undefined) result.image = chapter.image;

  return result;
}

// Convert API DailyStats to Frontend DailyStats
export function mapDailyStatsFromApi(apiStats: ApiDailyStats): DailyStats {
  return {
    date: apiStats.date,
    wordsWritten: apiStats.wordsWritten,
    timeSpent: apiStats.timeSpent,
    sessionsCount: apiStats.sessionsCount,
  };
}

// Create project data for API
export interface CreateProjectApiData {
  title: string;
  template?: string;
}

export function createProjectApiData(
  metadata?: Partial<BookMetadata>,
  template?: string
): CreateProjectApiData {
  return {
    title: metadata?.title || 'Untitled',
    template: template || 'blank',
  };
}

// Create chapter data for API
export interface CreateChapterApiData {
  projectId: string;
  title: string;
  sectionType?: string;
  content?: string;
}

export function createChapterApiData(
  projectId: string,
  title: string,
  sectionType: SectionType = 'chapter'
): CreateChapterApiData {
  return {
    projectId,
    title,
    sectionType,
    content: '',
  };
}
